/**
 * フレームキャプチャ＆サンプリングコンポーネント (FrameCapturer)
 * aidlc-docs/construction/media_export/docs/frame_capturer_component.md 準拠
 */

export interface CaptureOptions {
  /** 録画対象キャンバス */
  sourceCanvas: HTMLCanvasElement;
  /** 録画継続時間 (ミリ秒, 例: 3000ms) */
  durationMs: number;
  /** 目標フレームレート (既定値: 30fps, 最大: 60fps) */
  fps?: number;
  /** 進行状況コールバック */
  onProgress?: (progress: { stage: 'capturing'; progress: number }) => void;
  /** テスト用ヘルパー: 時間取得関数 (既定: performance.now) */
  nowFn?: () => number;
  /** テスト用ヘルパー: 作業用Canvas生成関数 */
  createCanvasFn?: (width: number, height: number) => HTMLCanvasElement;
}

export interface CapturedFrame {
  /** フレーム番号 (0-indexed) */
  frameIndex: number;
  /** 録画開始からの経過ミリ秒 */
  timestampMs: number;
  /** ピクセル生データ (RGBA) */
  imageData: ImageData;
  /** フレーム幅 (px) */
  width: number;
  /** フレーム高さ (px) */
  height: number;
}

export interface CaptureSequenceResult {
  /** 収集された全フレーム配列 */
  frames: CapturedFrame[];
  /** 実際の総フレーム数 */
  totalFrames: number;
  /** 実際の総録画時間 (ms) */
  actualDurationMs: number;
  /** フレームあたりの目標間隔 (ms) */
  frameIntervalMs: number;
  /** 各フレームの幅 */
  width: number;
  /** 各フレームの高さ */
  height: number;
  /** バッファ一括解放関数 */
  dispose: () => void;
}

export class FrameCapturer {
  private static readonly DEFAULT_FPS = 30;
  private static readonly MAX_FPS = 60;
  private static readonly MIN_FPS = 1;
  private static readonly MAX_FRAMES = 300; // OOM防止ガード (最大300フレーム)

  private isRunning: boolean = false;
  private isCancelled: boolean = false;
  private animFrameId: number | null = null;
  private timeoutId: any = null;
  private buffer: CapturedFrame[] = [];

  /**
   * キャンバスから指定FPS・時間でフレーム列をキャプチャ
   */
  public async startCapture(options: CaptureOptions): Promise<CaptureSequenceResult> {
    if (this.isRunning) {
      throw new Error('FrameCapturer is already running.');
    }

    const {
      sourceCanvas,
      durationMs,
      fps = FrameCapturer.DEFAULT_FPS,
      onProgress,
      nowFn = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
      createCanvasFn = (w, h) => {
        if (typeof document !== 'undefined' && document.createElement) {
          const c = document.createElement('canvas');
          c.width = w;
          c.height = h;
          return c;
        }
        throw new Error('Canvas creation not supported in this environment without createCanvasFn.');
      },
    } = options;

    if (!sourceCanvas) {
      throw new Error('sourceCanvas is required.');
    }
    if (durationMs <= 0) {
      throw new Error('durationMs must be greater than 0.');
    }

    const clampedFps = Math.max(FrameCapturer.MIN_FPS, Math.min(FrameCapturer.MAX_FPS, fps));
    const frameIntervalMs = 1000 / clampedFps;
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;

    if (width <= 0 || height <= 0) {
      throw new Error(`Invalid canvas dimensions: ${width}x${height}`);
    }

    this.isRunning = true;
    this.isCancelled = false;
    this.buffer = [];

    // 作業用2Dコンテキスト（WebGLキャンバスからでもdrawImage経由で安全にImageDataを抽出可能）
    let helperCanvas: HTMLCanvasElement;
    let helperCtx: CanvasRenderingContext2D | null = null;
    try {
      helperCanvas = createCanvasFn(width, height);
      helperCtx = helperCanvas.getContext('2d');
    } catch {
      helperCtx = null;
    }

    return new Promise<CaptureSequenceResult>((resolve, reject) => {
      const startTime = nowFn();
      let lastCaptureTime = -frameIntervalMs; // 初回フレームを即時キャプチャ可能にする
      let frameIndex = 0;

      const captureFrame = (currentTime: number) => {
        if (this.isCancelled) {
          this.cleanup();
          reject(new Error('Export cancelled'));
          return;
        }

        const elapsed = currentTime - startTime;

        // 目標フレーム間隔を超過している場合にサンプリング
        if (elapsed - lastCaptureTime >= frameIntervalMs) {
          const frameImageData = this.extractImageData(sourceCanvas, helperCanvas, helperCtx, width, height);
          if (frameImageData) {
            this.buffer.push({
              frameIndex,
              timestampMs: elapsed,
              imageData: frameImageData,
              width,
              height,
            });
            frameIndex++;
          }
          lastCaptureTime = elapsed;

          // 進捗通知
          const progress = Math.min(1.0, elapsed / durationMs);
          onProgress?.({ stage: 'capturing', progress });
        }

        // 終了条件判定: 制限時間経過または最大フレーム数到達
        if (elapsed >= durationMs || this.buffer.length >= FrameCapturer.MAX_FRAMES) {
          // 最終進捗通知
          onProgress?.({ stage: 'capturing', progress: 1.0 });

          const totalFrames = this.buffer.length;
          const actualDurationMs = elapsed;
          const capturedFrames = [...this.buffer];

          this.cleanup();

          resolve({
            frames: capturedFrames,
            totalFrames,
            actualDurationMs,
            frameIntervalMs,
            width,
            height,
            dispose: () => {
              capturedFrames.length = 0;
            },
          });
          return;
        }

        // 次フレームのスケジュール
        if (typeof requestAnimationFrame !== 'undefined') {
          this.animFrameId = requestAnimationFrame(() => captureFrame(nowFn()));
        } else {
          // ヘッドレス環境・タイマーフォールバック
          const nextInterval = Math.max(1, Math.min(16, frameIntervalMs - (nowFn() - currentTime)));
          this.timeoutId = setTimeout(() => captureFrame(nowFn()), nextInterval);
        }
      };

      // キャプチャループ開始
      captureFrame(startTime);
    });
  }

  /**
   * 実行中のキャプチャを中断し、バッファを解放
   */
  public cancel(): void {
    this.isCancelled = true;
    this.cleanup();
    this.dispose();
  }

  /**
   * 蓄積されたフレームバッファを強制解放
   */
  public dispose(): void {
    this.buffer = [];
  }

  /**
   * 実行中フラグの取得
   */
  public getIsRunning(): boolean {
    return this.isRunning;
  }

  private cleanup(): void {
    this.isRunning = false;
    if (this.animFrameId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private extractImageData(
    sourceCanvas: HTMLCanvasElement,
    helperCanvas: HTMLCanvasElement | undefined,
    helperCtx: CanvasRenderingContext2D | null,
    width: number,
    height: number
  ): ImageData | null {
    try {
      // 1. 作業用コンテキストがある場合は drawImage 経由で取得（WebGLキャンバス等も安全にキャプチャ）
      if (helperCtx && helperCanvas) {
        helperCtx.clearRect(0, 0, width, height);
        helperCtx.drawImage(sourceCanvas, 0, 0, width, height);
        return helperCtx.getImageData(0, 0, width, height);
      }

      // 2. ソースキャンバス自身の 2D コンテキストから直接取得
      const sourceCtx = sourceCanvas.getContext?.('2d') as CanvasRenderingContext2D | null;
      if (sourceCtx && typeof sourceCtx.getImageData === 'function') {
        return sourceCtx.getImageData(0, 0, width, height);
      }

      // 3. フォールバック（ダミーImageData生成）
      if (typeof ImageData !== 'undefined') {
        return new ImageData(width, height);
      }
      return null;
    } catch {
      return null;
    }
  }
}
